import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { ERROR_CODE } from '@core/constants/error-code';
import { SNAME } from '@core/constants/storage';
import { APP_CONFIG, ENV_CONFIG } from '@core/interfaces/configs';
import { AppLoadingService } from '@core/services/app-loading.service';
import { FormStateService } from '@core/services/form-state.service';
import { LoggerService } from '@core/services/logger.service';
import { RouterService } from '@core/services/router.service';
import { TmxService } from '@core/services/tmx.service';
import { WindowService } from '@core/services/window.service';
import { APPCONFIG, ENVCONFIG, TMX } from '@core/tokens/configs';
import * as Utils from '@core/utils';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getFinancialInsitutions, setHeaderConfig } from '@store/core/core.actions';
import { HeaderConfig } from '@store/core/core.interface';
import {
  EMPTY,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { v4 } from 'uuid';
import { FooterComponent } from '@core/components/footer/footer.component';
import { NgClass } from '@angular/common';
import { PbbSpinnerComponent } from '@core/components';
const { version, apiVersion } = require('../../package.json');

@Component({
  selector: 'pbb-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,
    MatProgressSpinnerModule,
    RouterOutlet,
    TranslateModule,
    FooterComponent,
    NgClass,
    PbbSpinnerComponent,
  ],
})
export class AppComponent extends UntilDestroy implements OnInit {
  title = 'konek-app';
  hideFooter: boolean = false;
  enableThemeM3: boolean = true;
  isLandingPage: boolean = false;
  isCheckout: boolean = Utils.isCheckout();
  showSpinner$: Observable<boolean | undefined> = this.router.events.pipe(
    filter(
      (e) =>
        e instanceof GuardsCheckStart ||
        e instanceof NavigationStart ||
        e instanceof GuardsCheckEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationEnd
    ),
    map((e) => {
      if (this.formStateService.hasChange) {
        return false;
      }
      if (e instanceof GuardsCheckStart || e instanceof NavigationStart) {
        return true;
      }
      if (e instanceof GuardsCheckEnd || e instanceof NavigationCancel || e instanceof NavigationEnd) {
        return false;
      }
      this.cd.markForCheck();
      return false;
    }),
    startWith(false),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );
  showAppSpinner$ = this.appLoadingService.isLoading$;
  showAnySpinner$: Observable<boolean> = combineLatest([this.showSpinner$, this.showAppSpinner$]).pipe(
    map(([routerSpinner, globalSpinner]) => !!routerSpinner || globalSpinner),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );

  @HostListener('window:popstate', ['$event'])
  onBack(event: PopStateEvent) {
    if (this.formStateService.hasChange) {
      return;
    }
    const targetPath = this.location.pathname;
    if (!this.routerService.enableBack) {
      history.pushState(null, 'null', location.href);
      history.go(1);
    } else {
      this.router
        .navigate([targetPath], {
          replaceUrl: true,
        })
        .then((res) => {
          if (targetPath !== this.location.pathname) {
            this.location.replace(targetPath);
          }
        });
    }
  }

  constructor(
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef,
    private readonly windowService: WindowService,
    private readonly routerService: RouterService,
    private readonly store: Store,
    private readonly titleService: Title,
    private readonly translateService: TranslateService,
    @Inject(ENVCONFIG) private readonly environment: ENV_CONFIG,
    @Inject(APPCONFIG) private readonly appConfig: APP_CONFIG,
    @Inject(TMX) private readonly tmxService: TmxService,
    public readonly logger: LoggerService,
    private readonly formStateService: FormStateService,
    @Inject('LOCATION') private readonly location: Location,
    private readonly appLoadingService: AppLoadingService
  ) {
    super();
    router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitleKey = '';

          while (route.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot.data['pageTitleKey']) {
            routeTitleKey = route.snapshot.data['pageTitleKey'];
          }
          this.logger.log('NAVIGATION', 'TITLE', this.translateService.instant(routeTitleKey));
          return this.translateService.instant(routeTitleKey);
        })
      )
      .subscribe((title: string) => {
        if (title) {
          this.titleService.setTitle(`Konek - ${title}`);
        }
      });

    //Redirect if spinner stays for longer then x time
    this.showSpinner$
      .pipe(
        switchMap((isSpinning) => {
          if (isSpinning) {
            return timer(this.appConfig.splashTimeout);
          } else {
            return EMPTY;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.router.navigate([Utils.isCheckout() ? 'checkout' : 'user-portal', 'error', ERROR_CODE.SYSTEM_TRY_AGAIN]);
      });

    // generate correlationId for standalone routes
    if (!this.isCheckoutRoute() && sessionStorage.getItem(SNAME.CORRELATIONID) === null) {
      sessionStorage.setItem(SNAME.CORRELATIONID, v4());
    }

    if (!this.isStandAlonePageRoute()) {
      this.store.dispatch(getFinancialInsitutions());
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (this.isCheckoutRoute()) {
      this.windowService.fromMerchant(event);
    }

    if (typeof event.data === 'string' && event.data.includes('tmx')) {
      this.tmxService.messageRecevied(event.data);
    }
  }

  isCheckoutRoute(): boolean {
    return (this.location.href || '').includes('checkout');
  }

  isStandAlonePageRoute(): boolean {
    const pageRoutes = ['email-verification', 'pilot'];
    return pageRoutes.some((route) => (this.location.href || '').includes(route));
  }
  getLocation() {
    return this.location.href;
  }
  ngOnInit(): void {
    this.logger.log('VERSION', 'APP', version);
    this.logger.log('VERSION', 'SPEC', apiVersion);
    this.logger.log('CONFIG', 'ENV', this.environment);
    this.logger.log('CONFIG', 'APP', this.appConfig);

    this.routerService.routeData.pipe(takeUntil(this.destroy$)).subscribe((headerConfig: HeaderConfig) => {
      this.hideFooter = headerConfig.hideFooter ? headerConfig.hideFooter : false;
      this.store.dispatch(
        setHeaderConfig({
          headerConfig,
        })
      );
      // settting bg image 
      if ((this.location.href.includes('landing') && !this.location.href.includes('fi-enrolment-landing')) || this.location.href.includes('relanding') || this.location.href.endsWith('auth')) {
        console.log('Landing page detected, setting background image');
        this.isLandingPage = true;
      } else {
        this.isLandingPage = false;
      }
    });
  }
}
